import { useReactiveVar } from "@apollo/client";
import { currentConfigVar } from "apollo/cache/config";
import classNames from "classnames";
import _ from "lodash";
import moment from "moment";
import numeral from "numeral";
import { FC, forwardRef, LegacyRef } from "react";

interface FinancialStatementViewProps {
  ref?: LegacyRef<HTMLDivElement>;
  transactions: {
    processedAt: string;
    amount: number;
    type: string;
    balanceAfter: number;
    balanceBefore: number;
    itemDescription: string;
    itemReference: string;
    itemType: "Bill" | "Payment";
  }[];
  servicePoint: {
    customerOrganization?: {
      name: string;
    };
    customerRepresentative: {
      fullName: string;
      phoneNumber: string;
      emailAddress: string;
    };
    ghanaPostAddress: string;
    code: string;
    meterCode: string;
    geoCode: string;
    accountCode: string;
    tariffClassCode: string;
    tariffClassName: string;
    address: string;
  };
  fromDate?: string;
  toDate?: string;
  date?: string;
}

export const FinancialStatementView: FC<FinancialStatementViewProps> =
  forwardRef<HTMLDivElement, FinancialStatementViewProps>(
    ({ servicePoint, transactions, fromDate, toDate, date }, ref) => {
      const {  dateFormat } = useReactiveVar(currentConfigVar);
      const resolvedFromDate = fromDate
        ? moment(fromDate).format(dateFormat)
        : _.first(transactions)?.processedAt
          ? moment(_.first(transactions)?.processedAt).format(dateFormat)
          : moment().format(dateFormat);

      const resolvedToDate = toDate
        ? moment(toDate).format(dateFormat)
        : _.last(transactions)?.processedAt
          ? moment(_.last(transactions)?.processedAt).format(dateFormat)
          : moment().format(dateFormat);
      const resolvedStatementDate = date
        ? moment(date).format(dateFormat)
        : moment().format(dateFormat);
      return (
        <div ref={ref} className="flex flex-col flex-1 text-black">
          <div
            style={{ breakAfter: "always" }}
            className="print:h-[1754px] print:w-[1000px] overflow-hidden overflow-y-auto flex flex-col"
          >
            <div className="">
              <div className=" flex-row items-center space-x-4 justify-between p-[30px] bg-[#f7f7f7] hidden print:flex">
                <h1 className=" text-black font-extrabold text-2xl leading-tight">
                  STATEMENT OF <br /> ACCOUNT
                </h1>
                <img
                  alt="ECG Logo"
                  src={
                    "https://www.ecg.com.gh/images/site/official_ecg_logo.jpg"
                  }
                  className="border-2 border-white bg-ecg-primary-600  size-[60px]"
                />
              </div>
              <div className="p-[30px] gap-20 print:grid grid-cols-2 hidden">
                <div className=" space-y-1.5  text-xs">
                  <h6 className=" text-black     font-bold  leading-[1.2]">
                    {servicePoint?.customerRepresentative?.fullName || "N/A"}
                  </h6>
                  <p className=" text-black  font-normal  leading-[1.2]">
                    {servicePoint?.customerRepresentative?.phoneNumber || "N/A"}
                  </p>
                  <p className=" text-black  font-normal  leading-[1.2]">
                    {servicePoint?.address || "N/A"}
                  </p>{" "}
                  <div className="text-black  leading-[1.2]">
                    <span className="font-bold">Account Number: </span>
                    <span className=" font-normal ">
                      {servicePoint?.accountCode || "N/A"}
                    </span>
                  </div>
                  <div className="text-black  leading-[1.2]">
                    <span className="font-bold">Service Point: </span>
                    <span className=" font-normal ">
                      {servicePoint?.code || "N/A"}
                    </span>
                  </div>
                  <div className="text-black  leading-[1.2]">
                    <span className="font-bold">Email: </span>
                    <span className=" font-normal ">
                      {servicePoint?.customerRepresentative?.emailAddress ||
                        "N/A"}
                    </span>
                  </div>
                </div>
                <div className=" space-y-1.5 text-xs ">
                  <div className="text-black  leading-[1.2]">
                    <span className="font-bold">Statement Date: </span>
                    <span className=" font-normal ">
                      {resolvedStatementDate}
                    </span>
                  </div>
                  <div className="text-black  leading-[1.2]">
                    <span className="font-bold">From: </span>
                    <span className=" font-normal ">{resolvedFromDate}</span>
                  </div>
                  <div className="text-black  leading-[1.2]">
                    <span className="font-bold">To: </span>
                    <span className=" font-normal ">{resolvedToDate}</span>
                  </div>
                </div>
              </div>

              <div className="flex-1 px-[30px] mt-[30px] print:mt-0 mb-[30px] grid grid-cols-5">
                <div className="col-span-5 flex flex-col space-y-4">
                  <div className="">
                    <div className="">
                      <table className="w-full border-collapse table-fixed">
                        <thead>
                        <tr className="bg-[#373737] ">
                          <th
                            scope="col"
                            className="px-2.5 py-2.5 text-left  text-xs max-w-min  font-semibold text-white "
                          >
                            Date
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-2.5 text-left text-xs w-[30%]  font-semibold text-white  "
                          >
                            Description
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-2.5 text-center text-xs font-semibold text-white  "
                          >
                            Reference
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-2.5 text-right text-xs font-semibold text-white  "
                          >
                            Debit (GHS)
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-2.5 text-right text-xs font-semibold text-white  "
                          >
                            Credit (GHS)
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-2.5 text-right text-xs font-semibold text-white  "
                          >
                            Balance (GHS)
                          </th>
                        </tr>
                        </thead>
                        <tbody>
                        <>
                          <tr className="bg-[#dbdbdb] font-bold">
                            <th
                              scope="col"
                              className="px-2.5 py-2.5  text-left text-xs font-semibold text-black         "
                            >
                              {resolvedFromDate}
                            </th>
                            <th
                              scope="col"
                              colSpan={2}
                              className="px-2.5 py-2.5 text-left text-xs font-semibold text-black "
                            >
                              Opening Balance
                            </th>
                            <th
                              scope="col"
                              className="px-2.5 py-2.5 text-right text-xs font-semibold text-black "
                            ></th>
                            <th
                              scope="col"
                              className="px-2.5 py-2.5 text-right text-xs font-semibold text-black "
                            ></th>
                            <th
                              scope="col"
                              className="px-2.5 py-2.5 text-right text-xs font-semibold text-black whitespace-nowrap w-[20%]"
                            >
                              {numeral(
                                _.first(transactions)?.balanceBefore || 0,
                              ).format("0,0.00")}
                            </th>
                          </tr>
                          {transactions?.map((item, idx) => (
                            <tr
                              key={idx}
                              className={classNames(
                                "border-b border-gray-200 even:bg-[#F5F5F5] font-normal  last:border-0",
                              )}
                            >
                              <td className="px-2.5 py-2.5 text-xs whitespace-nowrap">
                                {item?.processedAt
                                  ? moment(item?.processedAt).format(
                                    "DD/MM/YYYY",
                                  )
                                  : "N/A"}
                              </td>
                              <td className="px-2.5 py-2.5 text-left text-xs">
                                {item?.itemDescription || "N/A"}
                              </td>
                              <td className="px-2.5 py-2.5 text-center text-xs whitespace-nowrap">
                                {item?.itemType === "Bill"
                                  ? item?.itemReference
                                  ?.replace("NAMR", "")
                                  ?.replace("AMR", "") || "N/A"
                                  : [
                                  item?.itemReference?.slice(0, 6),
                                  "****",
                                  item?.itemReference?.slice(-7),
                                ].join("") || "N/A"}
                              </td>
                              <td
                                className={classNames(
                                  "px-2.5 py-2.5 text-right text-xs whitespace-nowrap",
                                  item?.type === "Debit"
                                    ? "text-green-500"
                                    : "",
                                )}
                              >
                                {item?.type === "Debit"
                                  ? numeral(item?.amount).format("0,0.00")
                                  : numeral(0).format("0,0.00")}
                              </td>
                              <td
                                className={classNames(
                                  "px-2.5 py-2.5 text-right text-xs whitespace-nowrap",
                                  item?.type === "Credit"
                                    ? "text-red-500"
                                    : "",
                                )}
                              >
                                {item?.type === "Credit"
                                  ? numeral(item?.amount).format("0,0.00")
                                  : numeral(0).format("0,0.00")}
                              </td>
                              <td className="px-2.5 py-2.5 text-right text-xs whitespace-nowrap">
                                {numeral(item?.balanceAfter).format("0,0.00")}
                              </td>
                            </tr>
                          ))}
                        </>
                        </tbody>
                        <tfoot>
                        <tr className="bg-[#dbdbdb] font-bold">
                          <th
                            scope="col"
                            className="px-2.5 py-2.5  text-left text-xs font-semibold text-black         "
                          >
                            {resolvedToDate}
                          </th>
                          <th
                            scope="col"
                            colSpan={2}
                            className="px-2.5 py-2.5 text-left text-xs font-semibold text-black "
                          >
                            Closing Balance
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-2.5 text-right text-xs font-semibold text-black "
                          >
                            {numeral(
                              _.sumBy(
                                _.filter(
                                  transactions,
                                  (t) => t.type === "Debit",
                                ),
                                "amount",
                              ),
                            ).format("0,0.00")}
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-2.5 text-right text-xs font-semibold text-black "
                          >
                            {numeral(
                              _.sumBy(
                                _.filter(
                                  transactions,
                                  (t) => t.type === "Credit",
                                ),
                                "amount",
                              ),
                            ).format("0,0.00")}
                          </th>
                          <th
                            scope="col"
                            className="px-2.5 py-2.5 text-right text-xs font-semibold text-black whitespace-nowrap w-[20%]"
                          >
                            {numeral(
                              _.last(transactions)?.balanceAfter || 0,
                            ).format("0,0.00")}
                          </th>
                        </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    },
  );
